import React from 'react';
import { Alert, Box, Button, ButtonBase, FormControl, InputLabel, MenuItem, Select, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowDownward, ArrowUpward, Remove, Refresh } from '@mui/icons-material';
import { describeLoadError, PERIOD_OPTIONS, type DrillSpec, type ExecTile, type PeriodKey } from './ownerData';
import { CHART_HEIGHT, INK, SEQUENTIAL, SERIES, TRACK } from '../chartTheme';
import { KpiTile, Panel } from '../DashboardKit';

// Colours, axis and legend styles live in ../chartTheme (shared with Tracking).

export type OnDrill = (spec: DrillSpec) => void;

/** The period dropdown, labelled the same way on every tab. */
export const PeriodSelect: React.FC<{ value: PeriodKey; onChange: (key: PeriodKey) => void; label?: string; id?: string }> = ({ value, onChange, label = 'Period', id = 'owner-period' }) => (
  <FormControl size="small" sx={{ minWidth: 240 }}>
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select labelId={`${id}-label`} id={id} value={value} label={label} onChange={(e) => onChange(e.target.value as PeriodKey)}>
      {PERIOD_OPTIONS.map((p) => (
        <MenuItem key={p.value} value={p.value}>
          {p.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/** A failed load in plain words, with a retry. */
export const LoadError: React.FC<{ what: string; message: string; onRetry: () => void; severity?: 'error' | 'warning'; children?: React.ReactNode }> = ({
  what,
  message,
  onRetry,
  severity = 'error',
  children,
}) => (
  <Alert
    severity={severity}
    action={
      <Button color="inherit" size="small" startIcon={<Refresh />} onClick={onRetry}>
        Try again
      </Button>
    }
  >
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      Could not load {what}.
    </Typography>
    <Typography variant="body2">
      {describeLoadError(message)}
      {children ? <> {children}</> : null}
    </Typography>
  </Alert>
);

/**
 * A headline number; when it has records behind it the whole tile opens them.
 * A tile whose figure is still loading shows a placeholder, and one whose call
 * failed says so and offers `onRetry` - it never spins forever.
 */
export const StatTile: React.FC<{ tile: ExecTile; onDrill: OnDrill; onRetry?: () => void }> = ({ tile, onDrill, onRetry }) => {
  const failed = tile.status === 'error';
  return (
    <KpiTile
      label={tile.label}
      value={
        tile.status === 'loading' ? (
          <Skeleton variant="text" width={96} sx={{ fontSize: '2.125rem', lineHeight: 1.2 }} aria-label={`Loading ${tile.label}`} />
        ) : failed ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {tile.value}
          </Box>
        ) : (
          tile.value
        )
      }
      delta={
        tile.delta && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: tile.delta.good == null ? INK.secondary : tile.delta.good ? INK.good : INK.bad }}>
          {tile.delta.direction === 'up' ? <ArrowUpward sx={{ fontSize: 16 }} /> : tile.delta.direction === 'down' ? <ArrowDownward sx={{ fontSize: 16 }} /> : <Remove sx={{ fontSize: 16 }} />}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            {tile.delta.text}
          </Typography>
        </Stack>
        )
      }
      detail={tile.detail}
      footer={
        failed && onRetry ? (
          <Button size="small" startIcon={<Refresh />} onClick={onRetry} sx={{ mt: 1, px: 0.5, fontWeight: 600 }}>
            Try again
          </Button>
        ) : undefined
      }
      onClick={tile.drill && !failed ? () => onDrill(tile.drill!) : undefined}
    />
  );
};

/** A number in a table or sentence that opens its records. */
export const DrillLink: React.FC<{ spec: DrillSpec | null | undefined; onDrill: OnDrill; children: React.ReactNode; align?: 'left' | 'right' }> = ({ spec, onDrill, children, align }) =>
  spec ? (
    <ButtonBase
      onClick={() => onDrill(spec)}
      sx={{
        font: 'inherit',
        color: 'primary.main',
        textDecoration: 'underline',
        textDecorationColor: (t) => alpha(t.palette.primary.main, 0.4),
        textUnderlineOffset: 3,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        textAlign: align === 'right' ? 'right' : 'left',
        borderRadius: 0.5,
        px: 0.25,
        mx: -0.25,
      }}
    >
      {children}
    </ButtonBase>
  ) : (
    <>{children}</>
  );

export const Section: React.FC<{ id: string; title: string; subtitle?: React.ReactNode; children: React.ReactNode }> = ({ id, title, subtitle, children }) => (
  <Box component="section" id={id} sx={{ mb: 5, scrollMarginTop: 72 }}>
    <Typography variant="h5" component="h2" sx={{ mb: subtitle ? 0.5 : 2 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '110ch' }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

type ChartHeight = number | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number>>;

/** A titled card holding one chart; the plot grows a little on large monitors. */
export const ChartCard: React.FC<{ title: string; subtitle?: React.ReactNode; children: React.ReactNode; empty?: string | null; height?: ChartHeight; footer?: React.ReactNode }> = ({
  title,
  subtitle,
  children,
  empty,
  height = CHART_HEIGHT,
  footer,
}) => (
  <Panel title={title} subtitle={subtitle}>
    {empty ? (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <Typography color="text.secondary">{empty}</Typography>
      </Box>
    ) : (
      <Box sx={{ height }}>{children}</Box>
    )}
    {footer && <Box sx={{ mt: 1 }}>{footer}</Box>}
  </Panel>
);

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Day-of-week x hour grid, sequential blue (darker = more). Office hours are
 * outlined so the reader sees what falls outside them; every cell has a
 * tooltip with its count.
 */
export const Heatmap: React.FC<{ grid: number[][]; max: number; unit: string }> = ({ grid, max, unit }) => {
  const colour = (n: number) => {
    if (n === 0 || max === 0) return TRACK;
    const idx = Math.min(SEQUENTIAL.length - 1, Math.floor((n / max) * (SEQUENTIAL.length - 1)));
    return SEQUENTIAL[Math.max(0, idx)];
  };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '40px repeat(24, minmax(10px, 1fr))', gap: '2px', minWidth: 340, alignItems: 'center' }}>
        <Box />
        {Array.from({ length: 24 }, (_, h) => (
          <Typography key={h} variant="caption" sx={{ color: INK.muted, textAlign: 'center', whiteSpace: 'nowrap', visibility: h % 3 === 0 ? 'visible' : 'hidden' }}>
            {String(h).padStart(2, '0')}
          </Typography>
        ))}
        {grid.map((row, d) => (
          <React.Fragment key={d}>
            <Typography variant="caption" sx={{ color: INK.secondary }}>
              {DAYS[d]}
            </Typography>
            {row.map((n, h) => {
              const office = d < 5 && h >= 9 && h < 18;
              return (
                <Tooltip key={h} title={`${DAYS[d]} ${String(h).padStart(2, '0')}:00-${String(h).padStart(2, '0')}:59 · ${n} ${unit}`} arrow disableInteractive>
                  <Box
                    tabIndex={0}
                    aria-label={`${DAYS[d]} ${h}:00, ${n} ${unit}`}
                    sx={{
                      height: { xs: 16, xl: 20 },
                      borderRadius: '2px',
                      bgcolor: colour(n),
                      outline: office ? `1px solid ${alpha(INK.primary, 0.25)}` : 'none',
                      outlineOffset: '-1px',
                      '&:hover, &:focus': { outline: `2px solid ${INK.primary}`, outlineOffset: '-2px' },
                    }}
                  />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Fewer
        </Typography>
        <Box sx={{ display: 'flex', gap: '2px' }}>
          {[0, 2, 4, 6, 8, 10, 12].map((i) => (
            <Box key={i} sx={{ width: 14, height: 10, borderRadius: '2px', bgcolor: SEQUENTIAL[i] }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          More · outlined cells are office hours (Mon-Fri 09:00-18:00)
        </Typography>
      </Stack>
    </Box>
  );
};

/** A small revenue trend line; gaps (no orders on file) break the line. */
export const Sparkline: React.FC<{ points: Array<number | null>; width?: number; height?: number; label: string }> = ({ points, width = 96, height = 26, label }) => {
  const values = points.filter((v): v is number => v != null);
  if (values.length === 0) return <Typography variant="caption" color="text.secondary">no orders</Typography>;
  const max = Math.max(...values, 1);
  const step = points.length > 1 ? (width - 6) / (points.length - 1) : 0;
  const y = (v: number) => height - 3 - (v / max) * (height - 6);
  const segments: string[] = [];
  let current = '';
  points.forEach((v, i) => {
    if (v == null) {
      if (current) segments.push(current);
      current = '';
      return;
    }
    current += `${current ? 'L' : 'M'}${(3 + i * step).toFixed(1)},${y(v).toFixed(1)}`;
  });
  if (current) segments.push(current);
  const lastIndex = points.length - 1 - [...points].reverse().findIndex((v) => v != null);
  return (
    <svg width={width} height={height} role="img" aria-label={label}>
      <title>{label}</title>
      {segments.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={SERIES.system} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {lastIndex >= 0 && points[lastIndex] != null && <circle cx={3 + lastIndex * step} cy={y(points[lastIndex] as number)} r={3} fill={SERIES.system} stroke="#fff" strokeWidth={1.5} />}
    </svg>
  );
};
